import {
    GET_FORMAT_CATEGORIES,
    GET_PROGRAM_CATEGORIES,
    GET_PROGRAM_CATEGORY_DETAILS,
} from '@/graphql/queries/categories.graphql';
import { apolloClient } from '@/lib/apollo-client';
import type { CategoryOption, ProgramCategoryDetails } from '@/types/categories';

type FetchCategoriesListResult<TCategory extends CategoryOption = CategoryOption> = {
    data: TCategory[];
    total: number;
};

type CategoriesQuery =
    | typeof GET_PROGRAM_CATEGORIES
    | typeof GET_PROGRAM_CATEGORY_DETAILS
    | typeof GET_FORMAT_CATEGORIES;

const CATEGORIES_CACHE_TTL_MS = 24 * 3600 * 1000;
const categoriesCache = new Map<
    string,
    {
        value: FetchCategoriesListResult;
        expiresAt: number;
    }
>();
const inFlightCategoriesRequests = new Map<string, Promise<FetchCategoriesListResult>>();

const getLocaleCacheKey = () => {
    if (typeof document === 'undefined') {
        return 'server';
    }

    return (document.documentElement.lang || 'unknown').toLowerCase();
};

async function fetchCategoriesList<TCategory extends CategoryOption>(
    cacheKeyPrefix: string,
    query: CategoriesQuery,
): Promise<FetchCategoriesListResult<TCategory>> {
    const cacheKey = `${cacheKeyPrefix}:${getLocaleCacheKey()}`;
    const now = Date.now();
    const cachedEntry = categoriesCache.get(cacheKey);
    if (cachedEntry && cachedEntry.expiresAt > now) {
        return cachedEntry.value as FetchCategoriesListResult<TCategory>;
    }

    const inFlightRequest = inFlightCategoriesRequests.get(cacheKey);
    if (inFlightRequest) {
        return inFlightRequest as Promise<FetchCategoriesListResult<TCategory>>;
    }

    const requestPromise: Promise<FetchCategoriesListResult<TCategory>> = apolloClient
        .query({
            query,
        })
        .then((response) => {
            const categories = (response.data.categories ?? []) as TCategory[];
            const result: FetchCategoriesListResult<TCategory> = {
                data: categories,
                total: categories.length,
            };

            categoriesCache.set(cacheKey, {
                value: result,
                expiresAt: Date.now() + CATEGORIES_CACHE_TTL_MS,
            });

            return result;
        })
        .finally(() => {
            inFlightCategoriesRequests.delete(cacheKey);
        });

    inFlightCategoriesRequests.set(cacheKey, requestPromise as Promise<FetchCategoriesListResult>);

    return requestPromise;
}

export function fetchProgramCategoriesList() {
    return fetchCategoriesList<CategoryOption>('program', GET_PROGRAM_CATEGORIES);
}

export function fetchProgramCategoryDetailsList() {
    return fetchCategoriesList<ProgramCategoryDetails>('program-details', GET_PROGRAM_CATEGORY_DETAILS);
}

export function fetchFormatCategoriesList() {
    return fetchCategoriesList<CategoryOption>('format', GET_FORMAT_CATEGORIES);
}
